'use client';

import { FocusScope } from '@radix-ui/react-focus-scope';
import { type KeyboardEvent, memo, type ReactNode, useCallback, useRef, useState } from 'react';

import { MenuIcon as Hamburger } from '@air/design-icons';
import { IconButton } from '@air/plugin-ui';

import { WorkspaceSidebar } from '~/app/workspace/_components/WorkspaceSidebar';
import { SIDEBAR_DEFAULT_WIDTH_PX } from '~/stores/useUiPreferencesStore';

export type WorkspaceSidebarShellProps = {
  children: ReactNode;
};

/**
 * Sidebar shell — collapsed rail + hover/click-to-open overlay. Focus
 * trapping, auto-focus on click-open, and focus restoration to the toggle
 * are handled by Radix FocusScope; we only manage open/close state and
 * Escape here.
 */
export const WorkspaceSidebarShell = memo(({ children }: WorkspaceSidebarShellProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  // Toggle-button opens (keyboard/click) trap + auto-focus the sidebar; hover
  // opens leave focus where it was so casual mouse users aren't yanked.
  const [trapFocus, setTrapFocus] = useState(false);

  const handleSidebarEnter = useCallback(() => setIsSidebarOpen(true), []);
  const handleSidebarLeave = useCallback(() => {
    setIsSidebarOpen(false);
    setTrapFocus(false);
  }, []);
  const handleSidebarToggle = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      setTrapFocus(next);
      return next;
    });
  }, []);

  const handleSidebarKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        handleSidebarLeave();
        // FocusScope's onUnmountAutoFocus restores to the trigger when trapped;
        // for hover-opens we restore manually so Esc still feels keyboard-friendly.
        if (!trapFocus) {
          toggleRef.current?.focus();
        }
      }
    },
    [handleSidebarLeave, trapFocus],
  );

  return (
    <>
      {/* Collapsed rail — always visible, shows a hamburger icon. w-10 ≡ 40px */}
      <div
        className="relative z-30 flex w-10 shrink-0 flex-col items-center border-neutral-4 border-r bg-neutral-2 pt-2"
        onMouseEnter={handleSidebarEnter}
      >
        <IconButton
          aria-controls="workspace-sidebar"
          aria-expanded={isSidebarOpen}
          aria-label="Toggle sidebar"
          onClick={handleSidebarToggle}
          ref={toggleRef}
        >
          <Hamburger aria-hidden="true" size="sm" />
        </IconButton>
      </div>

      {/* Expanded sidebar — overlays content on hover/click */}
      {isSidebarOpen && (
        <>
          {/* Backdrop to catch mouse leave. Purely decorative hit-target —
              aria-hidden so it doesn't show up as an unnamed interactive
              region (which also disarms the a11y key-with-click lint). */}
          <div
            aria-hidden="true"
            className="fixed inset-0 z-30"
            onClick={handleSidebarLeave}
            onMouseEnter={handleSidebarLeave}
          />
          <FocusScope asChild loop trapped={trapFocus}>
            {/* tabIndex={-1} lets FocusScope move focus *into* the aside on toggle-button
                opens. focus:outline-none hides the ring on the aside itself — only its
                child focusable elements should show focus, not the container. If a
                future code path programmatically focuses the aside, drop the
                focus:outline-none too. */}
            <aside
              aria-label="Workspace navigation"
              className="absolute top-0 bottom-0 left-0 z-40 flex h-full min-h-0 flex-col overflow-hidden shadow-popover focus:outline-none"
              id="workspace-sidebar"
              onKeyDown={handleSidebarKeyDown}
              onMouseLeave={handleSidebarLeave}
              style={{ width: SIDEBAR_DEFAULT_WIDTH_PX }}
              tabIndex={-1}
            >
              <WorkspaceSidebar />
            </aside>
          </FocusScope>
        </>
      )}

      {children}
    </>
  );
});

WorkspaceSidebarShell.displayName = 'WorkspaceSidebarShell';
